import React, { useState, useRef, useCallback, useEffect } from 'react';

const ProductImageCarousel = ({ product, className = '', autoPlay = false, interval = 3000 }) => {
  const allImages = [...new Set([product.image_url, ...(product.images || [])].filter(Boolean))];
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const timerRef = useRef(null);

  const scrollToIdx = useCallback((idx) => {
    if (!scrollRef.current) return;
    const { offsetWidth } = scrollRef.current;
    scrollRef.current.scrollTo({ left: idx * offsetWidth, behavior: 'smooth' });
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, offsetWidth } = scrollRef.current;
    setActiveIdx(Math.round(scrollLeft / offsetWidth));
  }, []);

  // Auto-play logic
  useEffect(() => {
    if (!autoPlay || allImages.length <= 1) return;

    const tick = () => {
      if (pausedRef.current) return;
      setActiveIdx(prev => {
        const next = (prev + 1) % allImages.length;
        scrollToIdx(next);
        return next;
      });
    };

    timerRef.current = setInterval(tick, interval);
    return () => clearInterval(timerRef.current);
  }, [autoPlay, allImages.length, interval, scrollToIdx]);

  // Pause on touch/mouse interaction
  const handleInteractionStart = useCallback(() => {
    pausedRef.current = true;
  }, []);

  const handleInteractionEnd = useCallback(() => {
    setTimeout(() => { pausedRef.current = false; }, 2000);
  }, []);

  if (allImages.length === 0) {
    return (
      <span className="text-3xl font-bold text-[var(--theme-primary)] opacity-20">
        {product.name?.charAt(0)}
      </span>
    );
  }

  if (allImages.length === 1) {
    return <img src={allImages[0]} alt={product.name} className={`w-full h-full object-cover ${className}`} />;
  }

  return (
    <div className="relative w-full h-full" data-testid={`img-carousel-${product.id}`}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onTouchStart={handleInteractionStart}
        onTouchEnd={handleInteractionEnd}
        onMouseDown={handleInteractionStart}
        onMouseUp={handleInteractionEnd}
        className="w-full h-full overflow-x-auto snap-x snap-mandatory flex"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {allImages.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`${product.name} ${i + 1}`}
            className={`w-full h-full object-cover flex-shrink-0 snap-center ${className}`}
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}
      </div>
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-[5]">
        {allImages.map((_, i) => (
          <div
            key={i}
            className={`rounded-full transition-all ${
              i === activeIdx ? 'w-2.5 h-1.5 bg-white shadow' : 'w-1.5 h-1.5 bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export { ProductImageCarousel };
